import { useCallback, ReactNode } from 'react';
import { Menu, Dropdown, Popconfirm, Tooltip, DropDownProps } from 'antd';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { useTranslate } from '@tolgee/react';
import Icon, { PoweroffOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  useMakeConversationCache,
  useConversationListFilter,
  useCurrentConversationIdValue,
} from 'hooks/business/conversation';
import { buyerNickMask } from 'services/channels/demo';
import useConversationOps from 'hooks/business/useConversationOps';
import { useOpenConversation } from 'hooks/business/conversationsHooks/useOpenConversation.ts';
import RemoveFromBlockList from 'views/Setting/BlockList/components/RemoveFromBlockList';
import AddToBlockList from 'views/Setting/BlockList/components/AddToBlockList';
import { starUIOps, starColors } from 'views/Conversation/ConvList/starData';
import {
  useSetBatchForwardEnable,
  useSetBatchForwardConvIds,
  useConvCanNotForward,
} from 'hooks/business/conversationsHooks/useConversationBatchForward';
import { ReactComponent as SvgFilterUnread } from './assets/filterUnread.svg';
import { ReactComponent as SvgDel } from './assets/del.svg';
import { ReactComponent as SvgForward } from './assets/forward.svg';
import { ReactComponent as SvgForwardBatch } from './assets/forwardBatch.svg';
import { ReactComponent as SvgRestart } from './assets/restart.svg';
import LockHandler from 'components/LockHandler';
import { useConvReceptionClose, useConvReceptionReopen } from 'hooks/business/useConvReception';
import { ConversationCategoryType } from 'hooks/business/conversationsHooks/useConversationCategory';

const cssMenu = css`
  box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%),
    0 9px 28px 8px rgb(0 0 0 / 5%) !important;
  .ant-dropdown-menu-title-content {
    color: #84868c !important;
    .anticon {
      margin-right: 2px;
    }
  }
`;

const dividerCls = css`
  width: 100%;
  margin: 4px 0;
  height: 1px;
  padding: 0;
  box-shadow: inset 0 -1px 0 0 rgba(240, 242, 245, 1);
`;

const Disabled = styled.div`
  color: #b5b9bf;
  cursor: not-allowed;
  > span {
    color: #b5b9bf;
  }
`;

const topIndex = 99999;

interface Props extends Omit<DropDownProps, 'overlay'> {
  talk: Conversation.Conversation;
  children?: ReactNode;
  onForward?: (conversation: Conversation.Conversation) => void;
  category: ConversationCategoryType;
}

/** 会话操作: 转发,未读,星标,删除 */
function ConvOps({ category, onForward, talk, ...otherProps }: Props) {
  const { t } = useTranslate();
  const setConversation = useMakeConversationCache('set');
  const delConversation = useMakeConversationCache('del');
  const openConversation = useOpenConversation();
  const clickedConvId = useCurrentConversationIdValue();
  const { modifyConversation } = useConversationOps();
  const convList = useConversationListFilter();

  const convCanNotForward = useConvCanNotForward();
  const setBatchForwardEnable = useSetBatchForwardEnable();
  const setBatchForwardConvIds = useSetBatchForwardConvIds();
  const closeReception = useConvReceptionClose();
  const reopenReception = useConvReceptionReopen();

  /** 删除后获取紧邻的会话标签,优先下面标签(下面无时使用上面的) */
  const getNextConv = useCallback(
    (id: string) => {
      let find = false;
      let nextConv;
      let idx = -1;
      convList.some((v, i) => {
        idx = i;
        if (find) {
          nextConv = v;
          return true;
        }
        if (id === v.conversationId) {
          find = true;
        }
        return false;
      });
      if (!nextConv && idx > 0) {
        nextConv = convList[idx - 1];
      }
      return nextConv;
    },
    [convList],
  );

  const handleMenuClick = useCallback(({ domEvent }: MenuInfo) => {
    domEvent.stopPropagation();
  }, []);

  const canNotForward = convCanNotForward(talk.conversationId);
  const hideDivider =
    canNotForward &&
    (talk.supervisePower || !talk.controlStatus || talk.conversationId === clickedConvId) &&
    talk.blacklistFlag;

  const startUI = talk.blacklistFlag
    ? null
    : starUIOps.map((colorAsterisk) => {
        if (colorAsterisk === 'NO_STAR' && talk.colorAsterisk === 'NO_STAR') {
          return null;
        }

        return (
          <Menu.Item
            key={`${colorAsterisk}`}
            onClick={() => {
              modifyConversation(
                talk.conversationId,
                {
                  asterisk: colorAsterisk,
                },
                () => {
                  setConversation({ ...talk, colorAsterisk });
                },
              );
            }}
          >
            {colorAsterisk === 'NO_STAR' ? (
              <StarOutlined style={{ marginRight: '6px' }} />
            ) : (
              <StarFilled style={{ color: starColors[colorAsterisk], marginRight: '6px' }} />
            )}
            {t(`filter.${colorAsterisk}`)}
          </Menu.Item>
        );
      });

  const menu =
    category === 'all' ? (
      <Menu onClick={handleMenuClick} className={cssMenu}>
        {startUI}
      </Menu>
    ) : (
      <Menu onClick={handleMenuClick} className={cssMenu}>
        {!talk.controlStatus || talk.conversationId === clickedConvId ? null : (
          <Menu.Item
            onClick={() => {
              modifyConversation(talk.conversationId, { read: talk.unreadCount > 0 ? '1' : '0' });
            }}
            key={`${t('scene.chatWorkspace.conversationList.action.markAsUnread')}`}
            data-key="convOpsUnread"
          >
            <Icon component={SvgFilterUnread} />{' '}
            {talk.unreadCount > 0
              ? t('scene.chatWorkspace.conversationList.action.markAsRead')
              : t('scene.chatWorkspace.conversationList.action.markAsUnread')}
          </Menu.Item>
        )}

        {startUI}

        {canNotForward ? null : (
          <>
            <Menu.Item
              disabled={talk.controlStatus === false}
              key={`${t('scene.chatWorkspace.conversationList.action.transfer')}`}
              onClick={() => {
                onForward?.(talk);
              }}
            >
              <Icon component={SvgForward} />{' '}
              {t('scene.chatWorkspace.conversationList.action.transfer')}
            </Menu.Item>
            <Menu.Item
              disabled={talk.controlStatus === false}
              key={`${t('scene.chatWorkspace.conversationList.action.batchTransfer')}`}
              onClick={() => {
                setBatchForwardConvIds(talk.conversationId);
                setBatchForwardEnable(true);
              }}
            >
              <Icon component={SvgForwardBatch} />{' '}
              {t('scene.chatWorkspace.conversationList.action.batchTransfer')}
            </Menu.Item>
          </>
        )}

        {hideDivider ? null : (
          <Menu.Item
            className={dividerCls}
            key="divider"
            onClick={(e) => e.domEvent.stopPropagation()}
          />
        )}
        <Menu.Item key={`${t('scene.chatWorkspace.conversationList.action.deleteConversation')}`}>
          {talk.processed ? (
            <Popconfirm
              title={t('scene.chatWorkspace.conversationList.action.deleteConfirmTitle')}
              zIndex={topIndex}
              onConfirm={() => {
                modifyConversation(talk.conversationId, { delete: '1' }, () => {
                  let conv;
                  if (clickedConvId === talk.conversationId) {
                    conv = getNextConv(talk.conversationId);
                  }
                  delConversation(talk);
                  conv &&
                    openConversation(conv as unknown as Conversation.Conversation, {
                      category: 'mine',
                    });
                });
              }}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <Icon component={SvgDel} />{' '}
                {t('scene.chatWorkspace.conversationList.action.deleteConversation')}
              </div>
            </Popconfirm>
          ) : (
            <Tooltip
              zIndex={topIndex}
              title={t('scene.chatWorkspace.conversationList.action.deleteDisabled')}
            >
              <Disabled>
                <Icon component={SvgDel} />{' '}
                {t('scene.chatWorkspace.conversationList.action.deleteConversation')}
              </Disabled>
            </Tooltip>
          )}
        </Menu.Item>
        {talk.channel === 'WA_BIZ' ? null : (
          <Menu.Item key="block-user">
            {talk.blacklistFlag ? (
              <RemoveFromBlockList
                blacklistId={talk.blacklistId!}
                buyerNick={buyerNickMask(talk.buyerNick)}
                storeId={talk.storeId}
                blockScope={talk.blacklistScope!}
                conversationId={talk.conversationId}
              />
            ) : (
              <AddToBlockList
                buyerId={talk.buyerId}
                buyerNick={buyerNickMask(talk.buyerNick)}
                storeId={talk.storeId}
                conversationId={talk.conversationId}
                channel={talk.channel}
              />
            )}
          </Menu.Item>
        )}
        {talk.inReception || !talk.processed ? (
          <LockHandler
            key="closeReception"
            Component={Menu.Item}
            handlerKey="onClick"
            onClick={() => closeReception(talk)}
          >
            <PoweroffOutlined />{' '}
            {t('scene.chatWorkspace.currentConversation.action.closeConversation')}
          </LockHandler>
        ) : (
          <LockHandler
            key="reopenReception"
            Component={Menu.Item}
            handlerKey="onClick"
            onClick={() => reopenReception(talk)}
          >
            <Icon component={SvgRestart} />{' '}
            {t('scene.chatWorkspace.currentConversation.action.reopenConversation')}
          </LockHandler>
        )}
      </Menu>
    );

  return <Dropdown overlay={menu} {...otherProps} />;
}

export default ConvOps;
